import { useEffect, useState } from "react";
import { Wallet, Save, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiErrorMessage, formatTanggalID } from "@/lib/api";

export function PayrollKonfigSection() {
  const [tipe, setTipe] = useState("Bulanan");
  const [cutoff, setCutoff] = useState("1");
  const [payday, setPayday] = useState("25");
  const [lemburOtomatis, setLemburOtomatis] = useState(true);
  const [pph21Aktif, setPph21Aktif] = useState(true);
  const [metodePph, setMetodePph] = useState("Dipotong Pegawai");
  const [potAlpha, setPotAlpha] = useState("15");
  const [potTelat, setPotTelat] = useState("7.5");
  const [klaimKePayroll, setKlaimKePayroll] = useState(true);
  const [klaimKenaPajak, setKlaimKenaPajak] = useState(false);
  const [aktif, setAktif] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/settings/payroll");
      setTipe(data.periode_tipe || "Bulanan");
      setCutoff(String(data.cutoff_start_day ?? 1));
      setPayday(String(data.payday ?? 25));
      setLemburOtomatis(data.lembur_otomatis !== false);
      setPph21Aktif(data.pph21_aktif !== false);
      setMetodePph(data.pph21_metode || "Dipotong Pegawai");
      setPotAlpha(String(data.potongan_alpha_persen ?? 15));
      setPotTelat(String(data.potongan_terlambat_persen ?? 7.5));
      setKlaimKePayroll(data.klaim_ke_payroll !== false);
      setKlaimKenaPajak(!!data.klaim_kena_pajak);
      setAktif(data.periode_aktif || null);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    const c = parseInt(cutoff, 10);
    const p = parseInt(payday, 10);
    if (isNaN(c) || c < 1 || c > 28) {
      toast.error("Cutoff harus tanggal 1-28");
      return;
    }
    if (isNaN(p) || p < 1 || p > 31) {
      toast.error("Tanggal gajian harus 1-31");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/payroll", {
        periode_tipe: tipe,
        cutoff_start_day: c,
        payday: p,
        lembur_otomatis: lemburOtomatis,
        pph21_aktif: pph21Aktif,
        pph21_metode: metodePph,
        potongan_alpha_persen: parseFloat(potAlpha || "0") || 0,
        potongan_terlambat_persen: parseFloat(potTelat || "0") || 0,
        klaim_ke_payroll: klaimKePayroll,
        klaim_kena_pajak: klaimKenaPajak,
      });
      setAktif(data.periode_aktif || null);
      toast.success("Periode penggajian disimpan");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="section-payroll">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700 shrink-0">
          <Wallet size={18} />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-extrabold text-slate-900 leading-tight">
            Periode Penggajian (Payroll)
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Menentukan rentang tarik data <b>Hari Hadir</b> &amp; <b>Total Jam Lembur</b> untuk penghitungan gaji.
            Cutoff 1 = kalender bulanan; contoh cutoff 21 → periode 21 s/d 20 bulan berikutnya.
            <b> Nama periode mengikuti bulan tanggal akhir cutoff</b> — 21 Jun s/d 20 Jul = "Payroll Juli".
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs font-bold text-slate-600">Tipe Periode</Label>
          <Select value={tipe} onValueChange={setTipe}>
            <SelectTrigger className="mt-1.5" data-testid="input-payroll-tipe"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Bulanan">Bulanan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold text-slate-600 flex items-center justify-between gap-2">
            <span>Cutoff Tanggal Awal</span>
            <span className="text-[10px] font-normal text-slate-400">1-28</span>
          </Label>
          <Input
            type="number"
            min="1"
            max="28"
            className="mt-1.5"
            value={cutoff}
            onChange={(e) => setCutoff(e.target.value)}
            data-testid="input-payroll-cutoff"
          />
        </div>
        <div>
          <Label className="text-xs font-bold text-slate-600 flex items-center justify-between gap-2">
            <span>Tanggal Gajian</span>
            <span className="text-[10px] font-normal text-slate-400">1-31</span>
          </Label>
          <Input
            type="number"
            min="1"
            max="31"
            className="mt-1.5"
            value={payday}
            onChange={(e) => setPayday(e.target.value)}
            data-testid="input-payroll-payday"
          />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-slate-100 pt-5">
        <div>
          <Label className="text-xs font-bold text-slate-600 flex items-center justify-between gap-2">
            <span>Potongan per Hari Alpha</span>
            <span className="text-[10px] font-normal text-slate-400">% tunjangan kehadiran</span>
          </Label>
          <Input
            type="number"
            step="0.5"
            min="0"
            max="100"
            className="mt-1.5"
            value={potAlpha}
            onChange={(e) => setPotAlpha(e.target.value)}
            data-testid="input-payroll-potongan-alpha"
          />
        </div>
        <div>
          <Label className="text-xs font-bold text-slate-600 flex items-center justify-between gap-2">
            <span>Potongan per Hari Terlambat</span>
            <span className="text-[10px] font-normal text-slate-400">% tunjangan kehadiran</span>
          </Label>
          <Input
            type="number"
            step="0.5"
            min="0"
            max="100"
            className="mt-1.5"
            value={potTelat}
            onChange={(e) => setPotTelat(e.target.value)}
            data-testid="input-payroll-potongan-terlambat"
          />
        </div>
        <p className="sm:col-span-2 text-[11px] text-slate-500 -mt-1">
          Dihitung dari komponen yang ditandai <b>Dasar Potongan Absensi</b> di Master Komponen, dan tidak pernah
          melebihi nilai tunjangan tersebut (maksimal habis / Rp 0).
        </p>
        <label className="flex items-start gap-3 text-sm text-slate-700 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <Switch
            checked={lemburOtomatis}
            onCheckedChange={setLemburOtomatis}
            data-testid="input-payroll-lembur-otomatis"
          />
          <span>
            <b>Upah Lembur Otomatis</b>
            <span className="block text-[11px] text-slate-500">
              Formula Kepmenaker 102/2004: upah sejam = (Gaji Pokok + tunjangan tetap) ÷ 173, jam pertama 1,5x, jam
              berikutnya 2x. Dilewati bila pegawai punya komponen <i>Berbasis Lembur</i>.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-3 text-sm text-slate-700 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <Switch checked={pph21Aktif} onCheckedChange={setPph21Aktif} data-testid="input-payroll-pph21" />
          <span>
            <b>Hitung PPh 21</b>
            <span className="block text-[11px] text-slate-500">
              Progresif Pasal 17 disetahunkan − PTKP pegawai, biaya jabatan 5% (maks Rp 500.000/bulan).
            </span>
          </span>
        </label>
        <div className="sm:col-span-2">
          <Label className="text-xs font-bold text-slate-600">Skema PPh 21</Label>
          <Select value={metodePph} onValueChange={setMetodePph} disabled={!pph21Aktif}>
            <SelectTrigger className="mt-1.5" data-testid="input-payroll-pph21-metode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Dipotong Pegawai">Dipotong Pegawai (mengurangi gaji bersih)</SelectItem>
              <SelectItem value="Ditanggung Perusahaan">Ditanggung Perusahaan (tidak mengurangi gaji bersih)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1.5">
            Bila <b>Ditanggung Perusahaan</b>, PPh 21 tetap dihitung dan ditampilkan, namun tidak dipotong dari gaji
            pegawai — nilainya otomatis masuk kolom <b>Beban Perusahaan</b> pada Laporan Gaji.
          </p>
        </div>

        <label className="flex items-start gap-3 text-sm text-slate-700 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <Switch
            checked={klaimKePayroll}
            onCheckedChange={setKlaimKePayroll}
            data-testid="input-payroll-klaim-ke-payroll"
          />
          <span>
            <b>Tunjangan Perawatan Khusus ke Payroll</b>
            <span className="block text-[11px] text-slate-500">
              Klaim Medical Reimbursement berstatus <i>Disetujui</i> dengan tanggal service di dalam
              cutoff periode otomatis muncul sebagai baris pendapatan <b>Tunjangan Perawatan Khusus</b>
              di slip gaji pegawai. Setelah payroll <i>Terbit</i>, klaim ditandai
              <b> Sudah Dibayar</b> agar tidak terhitung dua kali.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-3 text-sm text-slate-700 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <Switch
            checked={klaimKenaPajak}
            onCheckedChange={setKlaimKenaPajak}
            disabled={!klaimKePayroll}
            data-testid="input-payroll-klaim-kena-pajak"
          />
          <span>
            <b>Klaim Kena PPh 21</b>
            <span className="block text-[11px] text-slate-500">
              Bila aktif, nilai Tunjangan Perawatan Khusus ikut menambah dasar taxable PPh 21.
              Default: <b>tidak</b> — reimbursement non-taxable.
            </span>
          </span>
        </label>
      </div>

      {aktif ? (
        <div className="mt-4 rounded-md border border-teal-100 bg-teal-50/60 p-3 flex items-start gap-2 text-xs text-teal-900" data-testid="payroll-periode-aktif">
          <CalendarRange size={14} className="mt-0.5 shrink-0" />
          <div>
            Periode berjalan: <b>{aktif.label}</b> (cutoff {formatTanggalID(aktif.start)} – {formatTanggalID(aktif.end)}) ·
            Tanggal gajian <b>{formatTanggalID(aktif.tanggal_gajian)}</b>
          </div>
        </div>
      ) : null}

      <div className="mt-4">
        <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800 gap-1.5" data-testid="save-payroll-settings">
          <Save size={13} /> {saving ? "…" : "Simpan Periode"}
        </Button>
      </div>
    </div>
  );
}

export default PayrollKonfigSection;
